'use client';

import { Portal } from '@common/components';
import { cn } from '@core/utils';
import { motion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import {
  LOCAL_MARKET_ROUTES,
  ROUTE_TYPES,
  ROUTES,
  SELECTOR,
  SUB_DOMAIN,
} from '@/constants';
import { SAFE_AREA_BOTTOM_IOS } from '@/modules/wello-app/_hooks/useMountAppData';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';

import { LocalMarketBnb } from './LocalMarketBnb';
import styles from './LocalMarketBnbController.module.scss';

const DISPLAY_BNB_PATHNAMES = [LOCAL_MARKET_ROUTES.HOME.pathname] as const;

export const LocalMarketBnbController = () => {
  const pathname = usePathname();
  const isLoading = useAuthStore((state) => state.isLogin === undefined);
  const currentRouteType = useGlobalStore((state) => state.currentRouteType);
  const hideBottomNav = useGlobalStore((state) => state.hideBottomNav);
  const [displayBnb, setDisplayBnb] = useState(isLoading);

  useEffect(() => {
    const mainElement = document.getElementById(SELECTOR.MAIN);
    if (!mainElement || !pathname) {
      setDisplayBnb(false);

      return;
    }

    const isDisplayPath =
      DISPLAY_BNB_PATHNAMES.includes(
        pathname as (typeof DISPLAY_BNB_PATHNAMES)[number],
      ) ||
      (currentRouteType === ROUTE_TYPES.LOCAL_MARKET &&
        pathname === ROUTES.MY.pathname);

    setDisplayBnb(isDisplayPath);
  }, [pathname, currentRouteType]);

  const shouldShow = displayBnb && !hideBottomNav;

  const isSafeAreaBottomVersion = useDeviceInfoStore(
    (state) =>
      state.subDomain === SUB_DOMAIN.WELLO_APP &&
      state.osType === 'ios' &&
      !!state.appVersion &&
      state.appVersion >= '3.1.10',
  );

  return (
    <Portal renderTo={() => document.getElementById(SELECTOR.MAIN_WRAPPER)}>
      <motion.div
        animate={shouldShow ? 'show' : 'hide'}
        initial={isLoading ? 'show' : 'hide'}
        variants={{
          show: {
            opacity: 1,
            display: 'block',
          },
          hide: {
            opacity: 0,
            transitionEnd: {
              display: 'none',
            },
          },
        }}
        transition={{
          duration: 0.2,
        }}
        style={{
          bottom: isSafeAreaBottomVersion
            ? 10 + SAFE_AREA_BOTTOM_IOS
            : undefined,
        }}
        className={cn(styles.container, {
          'pointer-events-none': !shouldShow,
          'sr-only': isLoading,
        })}
      >
        <LocalMarketBnb isMounted={shouldShow} />
      </motion.div>
    </Portal>
  );
};
